import type { ShopTemplateBlock } from "./types";

export const shopAtomicBlockTypes = [
    "heading",
    "paragraph",
    "text",
    "quote",
    "code",
    "list",
    "checklist",
    "button",
    "table",
    "icon",
    "media",
    "uploadFile",
    "searchBar",
    "separator",
    "image",
    "video",
    "product",
    "input",
    "textarea",
    "accordion",
    "alert",
    "badge",
    "blockquote",
    "card",
    "column",
    "chart",
    "emoji",
    "countdown",
    "ctaBadge",
    "facebook",
    "field",
    "form",
    "iconList",
    "iconText",
    "instagram",
    "link",
    "map",
    "progressBar",
    "rating",
    "share",
    "signature",
    "socialMedia",
    "socialLink",
    "submitButton",
    "supportedPaymentButton",
    "textHighlight",
    "avatars",
    "appointmentButton",
    "donationButton",
    "addToCartButton",
    "headerLogo",
    "headerSearch",
    "headerStoresButton",
    "headerAccountButton",
    "headerLanguageSelector",
    "headerCurrencySelector",
    "headerFavoritesButton",
    "headerCartButton",
    "headerBenefitStrip",
    "serviceBenefitItem",
    "footerService",
    "footerLinks",
    "footerSocial",
    "footerApps",
    "footerCopyright",
] as const;

export type ShopAtomicBlockType = typeof shopAtomicBlockTypes[number];

export const SHOP_GRID_SLOT_KEY = "grid";
export const SHOP_GRID_CELL_WIDTH = 64;
export const SHOP_GRID_CELL_HEIGHT = 64;
export const SHOP_GRID_CELL_GAP = 24;
export const SHOP_PRESET_PART_OPTION = "presetPart";
export const SHOP_EDITOR_ORIGIN_OPTION = "editorOrigin";
export const SHOP_EDITOR_ORIGIN_PRESET = "preset";
export const SHOP_EDITOR_ORIGIN_USER = "user";
export const SHOP_EDITOR_INSERT_SOURCE_OPTION = "editorInsertSource";
export const SHOP_EDITOR_INSERT_SOURCE_SLASH = "slash";
export const SHOP_COLUMN_GROUP_OPTION = "columnGroup";
export const SHOP_DOCUMENT_ROLE_OPTION = "documentRole";
export const SHOP_DYNAMIC_ENTITY_FIELD_OPTION = "dynamicEntityField";
export const SHOP_DYNAMIC_TEXT_SOURCE_CUSTOM = "custom";
export const SHOP_EDITOR_COMMAND_LINE_OPTION = "editorCommandLine";
export const SHOP_EDITOR_PRESERVED_HEIGHT_OPTION = "editorPreservedHeight";
export const SHOP_EDITOR_PRESERVED_WIDTH_OPTION = "editorPreservedWidth";
export const SHOP_HERO_SLIDE_INDEX_OPTION = "heroSlideIndex";

export type ShopDynamicEntityType = "category" | "product" | "section";

export type ShopDynamicEntityField =
    | "description"
    | "descriptionShort"
    | "formattedPrice"
    | "name"
    | "nameMenu"
    | "reference";

export type ShopDynamicTextSource = typeof SHOP_DYNAMIC_TEXT_SOURCE_CUSTOM | ShopDynamicEntityField;

const shopDynamicEntityFields = new Set<ShopDynamicEntityField>([
    "description",
    "descriptionShort",
    "formattedPrice",
    "name",
    "nameMenu",
    "reference",
]);

const shopDynamicEntityFieldsByType: Readonly<Record<ShopDynamicEntityType, readonly ShopDynamicEntityField[]>> = {
    category: ["name", "nameMenu", "descriptionShort", "description"],
    product: ["name", "descriptionShort", "description", "reference", "formattedPrice"],
    section: ["name", "descriptionShort", "description"],
};

export function isShopDynamicEntityField(value: unknown): value is ShopDynamicEntityField {
    return typeof value === "string" && shopDynamicEntityFields.has(value as ShopDynamicEntityField);
}

export function shopDynamicTextSourcesForEntity(
    entityType: ShopDynamicEntityType,
): readonly ShopDynamicTextSource[] {
    return [SHOP_DYNAMIC_TEXT_SOURCE_CUSTOM, ...shopDynamicEntityFieldsByType[entityType]];
}

export type ShopBlockGridDefinition = {
    columns: number;
    rows: number;
    maxRows?: number;
    accepts?: string[];
};

export type ShopBlockFootprint = {
    w: number;
    h: number;
};

export type ShopBlockSlotDefinition = {
    key: string;
    accepts: string[];
    maxItems?: number;
    minItems?: number;
};

export type ShopBlockDefinition = {
    footprint?: ShopBlockFootprint;
    grid?: ShopBlockGridDefinition;
    type: string;
    slots?: ShopBlockSlotDefinition[];
};

const contentSlotAccepts = [
    "heading",
    "paragraph",
    "text",
    "quote",
    "code",
    "list",
    "checklist",
    "button",
    "table",
    "icon",
    "media",
    "uploadFile",
    "searchBar",
    "separator",
    "image",
    "video",
    "product",
    "input",
    "textarea",
    "accordion",
    "alert",
    "badge",
    "blockquote",
    "card",
    "column",
    "chart",
    "emoji",
    "countdown",
    "ctaBadge",
    "facebook",
    "form",
    "iconList",
    "iconText",
    "instagram",
    "link",
    "map",
    "progressBar",
    "rating",
    "share",
    "signature",
    "socialMedia",
    "socialLink",
    "supportedPaymentButton",
    "textHighlight",
    "avatars",
    "appointmentButton",
    "donationButton",
    "addToCartButton",
    "serviceBenefitItem",
];
const formSlotAccepts = [
    "field",
    "heading",
    "paragraph",
    "separator",
    "submitButton",
];
const columnSlotAccepts = contentSlotAccepts;
const mixedPresetSlotAccepts = contentSlotAccepts;
const mixedPresetSlotMaxItems = 64;
const blockquoteSlotAccepts = contentSlotAccepts.filter((type) => type !== "blockquote");

const defaultGridAccepts = [...contentSlotAccepts, "richContent", "contactInfo"];

const defaultContainerGrid: ShopBlockGridDefinition = {
    columns: 12,
    rows: 8,
    accepts: defaultGridAccepts,
};

const compactContainerGrid: ShopBlockGridDefinition = {
    columns: 12,
    rows: 8,
    accepts: defaultGridAccepts,
};

const richContentGrid: ShopBlockGridDefinition = {
    ...defaultContainerGrid,
    accepts: [...defaultGridAccepts, "catalogProductPage"],
};

const fullWidthFootprint: ShopBlockFootprint = { w: 12, h: 1 };

const shopBlockFootprints: Record<string, ShopBlockFootprint> = {
    accordion: { w: 6, h: 5 },
    accordionItem: { w: 6, h: 1 },
    addToCartButton: { w: 2, h: 1 },
    alert: { w: 4, h: 1 },
    appointmentButton: { w: 2, h: 1 },
    avatars: { w: 3, h: 1 },
    badge: { w: 2, h: 1 },
    blockquote: { w: 5, h: 3 },
    button: { w: 2, h: 1 },
    card: { w: 4, h: 4 },
    catalogProductPage: fullWidthFootprint,
    chart: { w: 6, h: 7 },
    column: { w: 4, h: 6 },
    countdown: { w: 3, h: 2 },
    ctaBadge: { w: 6, h: 1 },
    contactInfo: { w: 5, h: 3 },
    donationButton: { w: 2, h: 1 },
    facebook: { w: 2, h: 1 },
    footerApps: { w: 3, h: 2 },
    footerCopyright: { w: 12, h: 1 },
    footerLinks: { w: 3, h: 3 },
    footerService: { w: 3, h: 4 },
    footerSocial: { w: 3, h: 2 },
    field: { w: 6, h: 1 },
    form: { w: 6, h: 7 },
    headerAccountButton: { w: 1, h: 1 },
    headerBenefitStrip: { w: 8, h: 1 },
    headerCartButton: { w: 1, h: 1 },
    headerCurrencySelector: { w: 1, h: 1 },
    headerFavoritesButton: { w: 1, h: 1 },
    headerLanguageSelector: { w: 1, h: 1 },
    headerLogo: { w: 3, h: 1 },
    headerSearch: { w: 5, h: 1 },
    headerStoresButton: { w: 1, h: 1 },
    heading: { w: 4, h: 1 },
    icon: { w: 2, h: 1 },
    iconList: { w: 4, h: 2 },
    iconText: { w: 2, h: 1 },
    image: { w: 4, h: 4 },
    input: { w: 4, h: 1 },
    instagram: { w: 2, h: 1 },
    link: { w: 3, h: 1 },
    map: { w: 5, h: 3 },
    paragraph: { w: 4, h: 2 },
    product: { w: 4, h: 2 },
    progressBar: { w: 4, h: 1 },
    rating: { w: 3, h: 2 },
    richContent: { w: 6, h: 4 },
    searchBar: { w: 6, h: 1 },
    serviceBenefitItem: { w: 3, h: 2 },
    separator: fullWidthFootprint,
    share: { w: 6, h: 1 },
    socialLink: { w: 2, h: 1 },
    socialMedia: { w: 6, h: 1 },
    submitButton: { w: 6, h: 1 },
    supportedPaymentButton: { w: 4, h: 1 },
    text: { w: 4, h: 2 },
    textarea: { w: 4, h: 2 },
    textHighlight: { w: 6, h: 2 },
    video: { w: 5, h: 3 },
};

const shopBlockDefinitions: Record<string, ShopBlockDefinition> = {
    catalogProductPage: {
        footprint: shopBlockFootprints.catalogProductPage,
        type: "catalogProductPage",
        slots: [
            { key: "product", accepts: ["product"], maxItems: 1 },
        ],
    },
    searchBar: {
        footprint: shopBlockFootprints.searchBar,
        type: "searchBar",
        slots: [
            { key: "leadingIcon", accepts: ["icon"], maxItems: 1 },
            { key: "actionIcon", accepts: ["icon"], maxItems: 1 },
        ],
    },
    accordion: {
        footprint: { w: 6, h: 5 },
        type: "accordion",
        slots: [
            { key: "content", accepts: ["accordionItem", ...contentSlotAccepts], maxItems: mixedPresetSlotMaxItems },
        ],
    },
    header: {
        type: "header",
    },
    introHero: {
        footprint: fullWidthFootprint,
        grid: defaultContainerGrid,
        type: "introHero",
        slots: [
            { key: "content", accepts: mixedPresetSlotAccepts, maxItems: mixedPresetSlotMaxItems },
            { key: "actions", accepts: mixedPresetSlotAccepts, maxItems: mixedPresetSlotMaxItems },
            { key: "items", accepts: mixedPresetSlotAccepts, maxItems: mixedPresetSlotMaxItems },
            { key: "floatingCards", accepts: mixedPresetSlotAccepts, maxItems: mixedPresetSlotMaxItems },
            { key: "media", accepts: mixedPresetSlotAccepts, maxItems: mixedPresetSlotMaxItems },
        ],
    },
    columnsShowcase: {
        footprint: fullWidthFootprint,
        grid: defaultContainerGrid,
        type: "columnsShowcase",
        slots: [
            { key: "content", accepts: mixedPresetSlotAccepts, maxItems: mixedPresetSlotMaxItems },
            { key: "actions", accepts: mixedPresetSlotAccepts, maxItems: mixedPresetSlotMaxItems },
            { key: "items", accepts: mixedPresetSlotAccepts, maxItems: mixedPresetSlotMaxItems },
            { key: "media", accepts: mixedPresetSlotAccepts, maxItems: mixedPresetSlotMaxItems },
        ],
    },
    contentShowcase: {
        footprint: fullWidthFootprint,
        grid: defaultContainerGrid,
        type: "contentShowcase",
        slots: [
            { key: "content", accepts: mixedPresetSlotAccepts, maxItems: mixedPresetSlotMaxItems },
            { key: "actions", accepts: mixedPresetSlotAccepts, maxItems: mixedPresetSlotMaxItems },
            { key: "items", accepts: mixedPresetSlotAccepts, maxItems: mixedPresetSlotMaxItems },
            { key: "media", accepts: mixedPresetSlotAccepts, maxItems: mixedPresetSlotMaxItems },
        ],
    },
    heroSlider: {
        grid: defaultContainerGrid,
        type: "heroSlider",
        slots: [
            { key: SHOP_GRID_SLOT_KEY, accepts: mixedPresetSlotAccepts, maxItems: mixedPresetSlotMaxItems },
            { key: "content", accepts: mixedPresetSlotAccepts, maxItems: mixedPresetSlotMaxItems },
            { key: "floatingCards", accepts: mixedPresetSlotAccepts, maxItems: mixedPresetSlotMaxItems },
        ],
    },
    categoryHero: {
        type: "categoryHero",
        slots: [
            { key: "content", accepts: mixedPresetSlotAccepts, maxItems: mixedPresetSlotMaxItems },
        ],
    },
    categoryRail: {
        grid: defaultContainerGrid,
        type: "categoryRail",
        slots: [
            { key: "content", accepts: mixedPresetSlotAccepts, maxItems: mixedPresetSlotMaxItems },
            { key: "items", accepts: mixedPresetSlotAccepts, maxItems: mixedPresetSlotMaxItems },
            { key: "footer", accepts: mixedPresetSlotAccepts, maxItems: mixedPresetSlotMaxItems },
        ],
    },
    productRail: {
        grid: defaultContainerGrid,
        type: "productRail",
        slots: [
            { key: "content", accepts: mixedPresetSlotAccepts, maxItems: mixedPresetSlotMaxItems },
            { key: "afterRail", accepts: mixedPresetSlotAccepts, maxItems: mixedPresetSlotMaxItems },
        ],
    },
    recentlyViewedProducts: {
        grid: defaultContainerGrid,
        type: "recentlyViewedProducts",
        slots: [
            { key: "content", accepts: mixedPresetSlotAccepts, maxItems: mixedPresetSlotMaxItems },
            { key: "afterRail", accepts: mixedPresetSlotAccepts, maxItems: mixedPresetSlotMaxItems },
        ],
    },
    recommendedProducts: {
        grid: defaultContainerGrid,
        type: "recommendedProducts",
        slots: [
            { key: "content", accepts: mixedPresetSlotAccepts, maxItems: mixedPresetSlotMaxItems },
            { key: "afterRail", accepts: mixedPresetSlotAccepts, maxItems: mixedPresetSlotMaxItems },
        ],
    },
    campaignGrid: {
        grid: defaultContainerGrid,
        type: "campaignGrid",
        slots: [
            { key: "content", accepts: mixedPresetSlotAccepts, maxItems: mixedPresetSlotMaxItems },
            { key: "cards", accepts: mixedPresetSlotAccepts, maxItems: mixedPresetSlotMaxItems },
        ],
    },
    brandGrid: {
        grid: defaultContainerGrid,
        type: "brandGrid",
        slots: [
            { key: "content", accepts: mixedPresetSlotAccepts, maxItems: mixedPresetSlotMaxItems },
            { key: "media", accepts: mixedPresetSlotAccepts, maxItems: mixedPresetSlotMaxItems },
            { key: "items", accepts: mixedPresetSlotAccepts, maxItems: mixedPresetSlotMaxItems },
            { key: "highlights", accepts: mixedPresetSlotAccepts, maxItems: mixedPresetSlotMaxItems },
        ],
    },
    benefitStrip: {
        grid: defaultContainerGrid,
        type: "benefitStrip",
        slots: [
            { key: "content", accepts: mixedPresetSlotAccepts, maxItems: mixedPresetSlotMaxItems },
            { key: "cta", accepts: mixedPresetSlotAccepts, maxItems: mixedPresetSlotMaxItems },
            { key: "items", accepts: mixedPresetSlotAccepts, maxItems: mixedPresetSlotMaxItems },
            { key: "media", accepts: mixedPresetSlotAccepts, maxItems: mixedPresetSlotMaxItems },
        ],
    },
    serviceBenefits: {
        grid: defaultContainerGrid,
        type: "serviceBenefits",
        slots: [
            { key: "content", accepts: mixedPresetSlotAccepts, maxItems: mixedPresetSlotMaxItems },
            { key: "items", accepts: mixedPresetSlotAccepts, maxItems: mixedPresetSlotMaxItems },
            { key: "media", accepts: mixedPresetSlotAccepts, maxItems: mixedPresetSlotMaxItems },
        ],
    },
    newsletter: {
        grid: defaultContainerGrid,
        type: "newsletter",
        slots: [
            { key: "content", accepts: mixedPresetSlotAccepts, maxItems: mixedPresetSlotMaxItems },
        ],
    },
    footer: {
        type: "footer",
        slots: [
            { key: "columns", accepts: ["column"], maxItems: 4 },
            { key: "bottom", accepts: ["column"], maxItems: 3 },
        ],
    },
    footerService: {
        type: "footerService",
        slots: [
            { key: "content", accepts: ["column", "heading", "paragraph", "text", "link", "button", "socialLink"], maxItems: 8 },
        ],
    },
    footerLinks: {
        type: "footerLinks",
        slots: [
            { key: "items", accepts: ["link", "text", "socialLink", "button"], maxItems: 12 },
        ],
    },
    footerSocial: {
        type: "footerSocial",
    },
    socialMedia: {
        footprint: shopBlockFootprints.socialMedia,
        type: "socialMedia",
        slots: [
            { key: "items", accepts: ["socialLink"], maxItems: 12 },
        ],
    },
    footerApps: {
        type: "footerApps",
        slots: [
            { key: "items", accepts: ["text", "button"], maxItems: 6 },
        ],
    },
    footerCopyright: {
        type: "footerCopyright",
        slots: [
            { key: "items", accepts: ["link", "text", "socialLink", "button"], maxItems: 8 },
        ],
    },
    card: {
        footprint: shopBlockFootprints.card,
        type: "card",
        slots: [
            { key: "content", accepts: contentSlotAccepts, maxItems: mixedPresetSlotMaxItems },
        ],
    },
    badge: {
        footprint: shopBlockFootprints.badge,
        type: "badge",
        slots: [
            { key: "icon", accepts: ["icon"], maxItems: 1 },
        ],
    },
    ctaBadge: {
        footprint: shopBlockFootprints.ctaBadge,
        type: "ctaBadge",
        slots: [
            { key: "icon", accepts: ["icon"], maxItems: 2 },
        ],
    },
    blockquote: {
        footprint: shopBlockFootprints.blockquote,
        type: "blockquote",
        slots: [
            { key: "icon", accepts: ["icon"], maxItems: 1 },
            { key: "beforeText", accepts: blockquoteSlotAccepts, maxItems: mixedPresetSlotMaxItems },
            { key: "afterText", accepts: blockquoteSlotAccepts, maxItems: mixedPresetSlotMaxItems },
            { key: "beforeImage", accepts: blockquoteSlotAccepts, maxItems: mixedPresetSlotMaxItems },
            { key: "media", accepts: ["image"], maxItems: 1 },
            { key: "afterImage", accepts: blockquoteSlotAccepts, maxItems: mixedPresetSlotMaxItems },
        ],
    },
    column: {
        footprint: shopBlockFootprints.column,
        type: "column",
        slots: [
            { key: "content", accepts: columnSlotAccepts, maxItems: mixedPresetSlotMaxItems },
        ],
    },
    share: {
        footprint: shopBlockFootprints.share,
        type: "share",
        slots: [
            { key: "items", accepts: ["icon"], minItems: 6, maxItems: 6 },
        ],
    },
    textHighlight: {
        footprint: shopBlockFootprints.textHighlight,
        type: "textHighlight",
        slots: [
            { key: "beforeTitle", accepts: contentSlotAccepts, maxItems: mixedPresetSlotMaxItems },
            { key: "afterTitle", accepts: contentSlotAccepts, maxItems: mixedPresetSlotMaxItems },
            { key: "content", accepts: contentSlotAccepts, maxItems: mixedPresetSlotMaxItems },
        ],
    },
    chart: {
        footprint: shopBlockFootprints.chart,
        type: "chart",
        slots: [
            { key: "afterTitle", accepts: contentSlotAccepts, maxItems: mixedPresetSlotMaxItems },
        ],
    },
    image: {
        footprint: shopBlockFootprints.image,
        type: "image",
    },
    product: {
        footprint: shopBlockFootprints.product,
        type: "product",
        slots: [
            { key: "image", accepts: ["image"], maxItems: 1 },
        ],
    },
    video: {
        footprint: shopBlockFootprints.video,
        type: "video",
    },
    field: {
        footprint: shopBlockFootprints.field,
        type: "field",
    },
    form: {
        footprint: shopBlockFootprints.form,
        type: "form",
        slots: [
            { key: "items", accepts: formSlotAccepts, maxItems: mixedPresetSlotMaxItems },
        ],
    },
    submitButton: {
        footprint: shopBlockFootprints.submitButton,
        type: "submitButton",
    },
    contactInfo: {
        grid: defaultContainerGrid,
        type: "contactInfo",
        slots: [
            { key: "details", accepts: contentSlotAccepts, maxItems: 4 },
        ],
    },
    richContent: {
        grid: richContentGrid,
        type: "richContent",
        slots: [
            { key: "content", accepts: mixedPresetSlotAccepts, maxItems: mixedPresetSlotMaxItems },
        ],
    },
    seoText: {
        grid: defaultContainerGrid,
        type: "seoText",
        slots: [
            { key: "content", accepts: mixedPresetSlotAccepts, maxItems: mixedPresetSlotMaxItems },
        ],
    },
};

export function getShopBlockDefinition(type: string): ShopBlockDefinition | null {
    const definition = shopBlockDefinitions[type];

    if (definition) {
        return definition;
    }

    const footprint = shopBlockFootprints[type];

    if (type === "input" || type === "textarea") {
        return {
            ...(footprint ? { footprint } : {}),
            type,
        };
    }

    if (footprint) {
        return {
            footprint,
            type,
        };
    }

    return {
        grid: compactContainerGrid,
        type,
    };
}

export function getShopBlockGrid(type: string): ShopBlockGridDefinition | null {
    return getShopBlockDefinition(type)?.grid ?? null;
}

export function getShopBlockFootprint(type: string): ShopBlockFootprint {
    return getShopBlockDefinition(type)?.footprint
        ?? shopBlockFootprints[type]
        ?? { w: 2, h: 1 };
}

export function getShopBlockSlots(type: string): ShopBlockSlotDefinition[] {
    return getShopBlockDefinition(type)?.slots ?? [];
}

export function getShopBlockSlot(type: string, key: string): ShopBlockSlotDefinition | null {
    return getShopBlockSlots(type).find((slot) => slot.key === key) ?? null;
}

export function slotBlocks(block: ShopTemplateBlock | null | undefined, key: string): ShopTemplateBlock[] {
    const slots = block?.slots;

    if (!slots || typeof slots !== "object") {
        return key === "children" && Array.isArray(block?.children) ? block.children : [];
    }

    const value = slots[key];

    return Array.isArray(value) ? value : [];
}

export type ShopHeroSliderLegacySlide = Readonly<{
    block: ShopTemplateBlock;
    slideIndex: number;
    sourceIndex: number;
    sourceSlot: "content" | typeof SHOP_GRID_SLOT_KEY;
}>;

export type ShopHeroSliderLegacyStructure = Readonly<{
    contentBlocks: ShopTemplateBlock[];
    gridBlocks: ShopTemplateBlock[];
    slides: ShopHeroSliderLegacySlide[];
}>;

function shopHeroSliderLegacySlideIndex(block: ShopTemplateBlock | null | undefined): number | null {
    const slideIndex = Number(block?.options?.[SHOP_HERO_SLIDE_INDEX_OPTION]);

    return block?.type === "column"
        && Number.isInteger(slideIndex)
        && slideIndex >= 1
        && slideIndex <= 3
        ? slideIndex
        : null;
}

/**
 * Splits the temporary schema-37 slide columns from Hero Slider's real surface slots.
 * Unindexed blocks always remain ordinary root content/grid blocks.
 */
export function splitShopHeroSliderLegacyStructure(
    block: ShopTemplateBlock | null | undefined,
): ShopHeroSliderLegacyStructure {
    const grid = slotBlocks(block, SHOP_GRID_SLOT_KEY);
    const content = slotBlocks(block, "content");
    const slides: ShopHeroSliderLegacySlide[] = [];
    const usedSlideIndexes = new Set<number>();
    const partitionLegacySlide = (
        childBlock: ShopTemplateBlock,
        sourceIndex: number,
        sourceSlot: ShopHeroSliderLegacySlide["sourceSlot"],
    ): boolean => {
        const slideIndex = shopHeroSliderLegacySlideIndex(childBlock);

        if (slideIndex === null || usedSlideIndexes.has(slideIndex)) {
            return true;
        }

        usedSlideIndexes.add(slideIndex);
        slides.push({ block: childBlock, slideIndex, sourceIndex, sourceSlot });

        return false;
    };
    const gridBlocks = grid.filter((childBlock, sourceIndex) => {
        return partitionLegacySlide(childBlock, sourceIndex, SHOP_GRID_SLOT_KEY);
    });
    const contentBlocks = content.filter((childBlock, sourceIndex) => {
        return partitionLegacySlide(childBlock, sourceIndex, "content");
    });

    return {
        contentBlocks,
        gridBlocks,
        slides: slides.sort((left, right) => left.slideIndex - right.slideIndex),
    };
}

function shopBlockOptionText(block: ShopTemplateBlock | null | undefined, key: string): string {
    const value = block?.options?.[key];

    return typeof value === "string" ? value.trim() : "";
}

export function getShopBlockPresetPart(block: ShopTemplateBlock | null | undefined): string {
    const presetPart = shopBlockOptionText(block, SHOP_PRESET_PART_OPTION);

    if (presetPart !== "") {
        return presetPart;
    }

    return shopBlockOptionText(block, "materializedRenderer") === "pricingComparisonCard"
        ? "pricingPlan"
        : "";
}
